import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  EmptyStateComponent,
  InformationBoxComponent,
} from '@principle-theorem/ng-principle-shared';
import {
  CustomReportsService,
  ReportEditorService,
  SelectReportTemplateCardComponent,
} from '@principle-theorem/ng-reporting';
import {
  InputSearchFilter,
  NgDialogModule,
  onRouteChange$,
  toSearchStream,
  TypedFormControl,
} from '@principle-theorem/ng-shared';
import {
  ICustomReport,
  IMarketingCampaign,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import {
  CustomReportTemplate,
  getReportBuilderTemplates,
  ICustomReportTemplate,
  REPORT_BUILDER_DATA_SOURCES,
  resolveDataSource,
} from '@principle-theorem/reporting';
import {
  ALL_REGIONS,
  multiFilter,
  multiMap,
  WithRef,
} from '@principle-theorem/shared';
import { sortBy } from 'lodash';
import { combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';

export interface ISelectCampaignReportDialogRequest {
  campaign: WithRef<IMarketingCampaign>;
}

export interface ISelectCampaignReportDialogResponse {
  option: IReportOption;
}

export interface IReportOption {
  customReport?: WithRef<ICustomReport>;
  template: ICustomReportTemplate;
}

@Component({
  selector: 'pr-select-campaign-report-dialog',
  templateUrl: './select-campaign-report-dialog.component.html',
  styleUrl: './select-campaign-report-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CustomReportsService, ReportEditorService],
  imports: [
    InformationBoxComponent,
    NgDialogModule,
    SelectReportTemplateCardComponent,
    EmptyStateComponent,
  ],
})
export class SelectCampaignReportDialogComponent {
  private _router = inject(Router);
  private _customReports = inject(CustomReportsService);
  private _dialogRef = inject(
    MatDialogRef<
      SelectCampaignReportDialogComponent,
      ISelectCampaignReportDialogResponse
    >
  );
  data = inject<ISelectCampaignReportDialogRequest>(MAT_DIALOG_DATA);

  searchCtrl = new TypedFormControl<string>('');
  filteredOptions: Signal<IReportOption[]>;

  constructor() {
    onRouteChange$(this._router)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this._dialogRef.close());

    const customReportOptions$ = this._customReports
      .getAvailableReports$()
      .pipe(multiMap((report) => this._customReportToOption(report)));

    const options$ = combineLatest([
      customReportOptions$,
      of(this._getReportTemplates()),
    ]).pipe(
      map(([customReportOptions, templateOptions]) =>
        [customReportOptions, templateOptions].flat()
      ),
      multiFilter((option) =>
        this._optionMatchesScope(option, this.data.campaign.scope)
      )
    );

    const searchFilter = new InputSearchFilter<IReportOption>(
      options$,
      toSearchStream(this.searchCtrl),
      [
        'template.name',
        'template.description',
        (option) => option.template.tags?.join(' ') ?? '',
      ]
    );
    const results$ = searchFilter.results$.pipe(
      map((options) => sortBy(options, ['customReport', 'template.name']))
    );
    this.filteredOptions = toSignal(results$, {
      initialValue: [],
    });
  }

  select(option: IReportOption): void {
    this._dialogRef.close({ option });
  }

  private _optionMatchesScope(
    option: IReportOption,
    mustMatchScope: TemplateScope
  ): boolean {
    const dataSource = resolveDataSource(
      option.template.dataSourceId,
      REPORT_BUILDER_DATA_SOURCES
    );
    const scopes = dataSource?.scopes ?? [];
    return scopes.includes(mustMatchScope);
  }

  private _customReportToOption(
    customReport: WithRef<ICustomReport>
  ): IReportOption {
    const template = {
      name: customReport.name,
      description: customReport.description ?? 'No Description',
      tags: ['Custom Report'],
      dataSourceId: customReport.dataSource,
      chartSections: customReport.chartSections,
      staticFilters: customReport.staticFilters,
      columns: customReport.columns,
      regions: ALL_REGIONS,
    };
    return { customReport, template };
  }

  private _getReportTemplates(): IReportOption[] {
    const reportTemplates = getReportBuilderTemplates();
    const basicTemplates = REPORT_BUILDER_DATA_SOURCES.filter(
      (dataSource) => !dataSource.isRetired
    ).map((dataSource) =>
      CustomReportTemplate.basicDataSourceTemplate(dataSource)
    );
    return [reportTemplates, basicTemplates]
      .flat()
      .map((template) => ({ template }));
  }
}
